import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextField from 'components/Input/CustomTextField';
import HospitalOrInstitutions from 'components/Global/HospitalOrInstitutions';
import PasswordTextField from 'components/Input/PasswordTextField';
import CustomButton from 'components/CustomInput/Button/index';
import { checkPasswordValidity, getClusterDisplayFromHospital, isAuthorizedEmail, produce } from 'utility/general';
import styled from 'styled-components';
import { UPDATE_AUTH_USER_PASSCODE, UPDATE_REMOTE_USER } from 'store/key';
import TextHeader from 'components/CustomText/TextHeader';
import useDialog from 'components/ConfirmDialog/useDialog';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import {
    CONFIRM_PASSCODE_NOT_MATCH,
    PASSCODE_POLICY_TIPS,
    PASSCODE_UPDATED,
    PROFILE_CONFIRM,
    PROFILE_UPDATED,
} from 'utility/frontendMessage';

const SListItem = styled.div`
    padding-top: 12px;
`;

const SItemLayout = styled.div`
    display: flex;
    margin-bottom: 15px;
    align-items: center;
`;

const SItemLabel = styled.div`
    width: 200px;
`;

const SValueLayout = styled.div`
    width: 400px;
`;

const TextFieldContainer = (props) => {
    const { label, children } = props;
    return (
        <SItemLayout>
            <SItemLabel>{label}:</SItemLabel>
            <SValueLayout>{children}</SValueLayout>
        </SItemLayout>
    );
};

const Profile = (props) => {
    const userDetail = useSelector((state) => state.user.detail);
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const showDialog = useDialog();

    const [userPostData, setUserPostData] = useState({});
    const [passwordPostData, setPasswordPostData] = useState({});
    const [formValidity, setFormValidity] = useState({});
    const [passwordValidity, setPasswordValidity] = useState({});
    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);

    const [forcePasswordValidationShow, setForcePasswordValidationShow] = useState(false);

    const postDataOnChange = (e) => {
        const { name, value } = e.target;

        console.log('155737 index.jsx value', name, value);

        setUserPostData(
            produce((draft) => {
                draft[name] = value;
            }),
        );
    };

    const onPasswordUpdate = (e) => {
        const { name, value } = e.target;

        setPasswordPostData(
            produce((draft) => {
                draft[name] = value;
            }),
        );
    };

    const resetData = () => {
        setUserPostData({});
    };

    const onProfileUpdate = async () => {
        const isFormValid = Object.values(formValidity).every((item) => item);

        if (!isFormValid) {
            // await showDialog({
            //     description: 'Please Check the form for completeness',
            //     disableCancelButton: true,
            // });
            setForceErrorDisplay(true);
            return;
        }

        const confirm = await showDialog({
            description: PROFILE_CONFIRM,
        });

        if (!confirm) {
            return;
        }

        dispatch({
            type: UPDATE_REMOTE_USER,
            payload: {
                ...userPostData,
                callback: async () => {
                    await showDialog({ description: PROFILE_UPDATED, disableCancelButton: true });
                    setUserPostData({});
                },
            },
        });
    };

    const onPasswordSubmit = async () => {
        const { newPassword, oldPassword, confirmNewPassword } = passwordPostData;

        const isPasswordFormValid = Object.values(passwordValidity).every((item) => item);

        if (!isPasswordFormValid) {
            setForcePasswordValidationShow(true);
            // await showDialog({
            //     description: 'Please Check the form for completeness',
            //     disableCancelButton: true,
            // });
            return;
        }

        if (!oldPassword || !newPassword) {
            return;
        }

        if (newPassword !== confirmNewPassword) {
            await showDialog({
                description: CONFIRM_PASSCODE_NOT_MATCH,
                disableCancelButton: true,
            });
            return;
        }

        if (!checkPasswordValidity(newPassword)) {
            await showDialog({
                description: PASSCODE_POLICY_TIPS,
                disableCancelButton: true,
            });

            return;
        }

        dispatch({
            type: UPDATE_AUTH_USER_PASSCODE,
            payload: {
                newPassword,
                oldPassword,
                callback: async () => {
                    await showDialog({
                        description: PASSCODE_UPDATED,
                        disableCancelButton: true,
                    });

                    setPasswordPostData({});
                },
            },
        });
    };

    const isChanged = () => {
        return Object.values(userPostData).length > 0;
    };

    return (
        <div style={{ padding: 15 }}>
            <TextHeader>User Profile</TextHeader>
            <div style={{ padding: '15px 0' }}>
                <TextFieldContainer label="Email">
                    <CustomTextField label="Email" disabled name="email" value={userDetail.email} showErrorWhenOnBlur />
                </TextFieldContainer>
                <TextFieldContainer label="Title">
                    <CustomTextField
                        label="Title"
                        name="title"
                        validators={['disableChinese', 'isRequired']}
                        forceErrorDisplay={forceErrorDisplay}
                        maxLength={255}
                        isValidValue={(isValid) => {
                            setFormValidity(
                                produce((draft) => {
                                    draft.title = isValid;
                                }),
                            );
                        }}
                        value={userPostData.title ?? userDetail.title ?? ''}
                        onChange={postDataOnChange}
                    />
                </TextFieldContainer>
                <TextFieldContainer label="First Name">
                    <CustomTextField
                        label="First Name"
                        name="firstName"
                        validators={['disableChinese', 'isRequired']}
                        forceErrorDisplay={forceErrorDisplay}
                        maxLength={255}
                        isValidValue={(isValid) => {
                            setFormValidity(
                                produce((draft) => {
                                    draft.firstName = isValid;
                                }),
                            );
                        }}
                        value={userPostData.firstName ?? userDetail.firstName ?? ''}
                        onChange={postDataOnChange}
                    />
                </TextFieldContainer>
                <TextFieldContainer label="Surname">
                    <CustomTextField
                        label="Surname"
                        name="surname"
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['disableChinese', 'isRequired']}
                        maxLength={255}
                        isValidValue={(isValid) => {
                            setFormValidity(
                                produce((draft) => {
                                    draft.firstName = isValid;
                                }),
                            );
                        }}
                        value={userPostData.surname ?? userDetail.surname ?? ''}
                        onChange={postDataOnChange}
                    />
                </TextFieldContainer>
                <TextFieldContainer label="Hospital / Institution">
                    <HospitalOrInstitutions
                        label="Hospital / Institution"
                        name="institution"
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['isRequired']}
                        isValidValue={(isValid) =>
                            setFormValidity(
                                produce((draft) => {
                                    draft.institution = isValid;
                                }),
                            )
                        }
                        isAuthorizedEmail={isAuthorizedEmail(userDetail.email.toLowerCase())}
                        hospitalOrInstitution={userPostData.institution ?? userDetail.institution}
                        setHospitalOrInstitution={postDataOnChange}
                    />
                </TextFieldContainer>
                <TextFieldContainer label="Contact Number">
                    <CustomTextField
                        label="Contact Number"
                        name="contact"
                        validators={['disableAlphabet', 'isRequired']}
                        maxLength={255}
                        forceErrorDisplay={forceErrorDisplay}
                        isValidValue={(isValid) =>
                            setFormValidity(
                                produce((draft) => {
                                    draft.contact = isValid;
                                }),
                            )
                        }
                        value={userPostData.contact ?? userDetail.contact ?? ''}
                        onChange={postDataOnChange}
                    />
                </TextFieldContainer>
                {isAuthorizedEmail(userDetail.email.toLowerCase()) && (
                    <TextFieldContainer label="Cluster">
                        <CustomTextField
                            disabled
                            label="Cluster"
                            name="cluster"
                            value={getClusterDisplayFromHospital(userPostData.institution ?? userDetail.institution)}
                            onChange={postDataOnChange}
                        />
                    </TextFieldContainer>
                )}

                <div style={{ display: 'flex', marginTop: 20 }}>
                    <CustomButton showMargin onClick={resetData} loading={user.isLoading}>
                        Reset
                    </CustomButton>
                    <CustomButton disabled={!isChanged()} onClick={onProfileUpdate} loading={user.isLoading}>
                        Update
                    </CustomButton>
                </div>
            </div>
            <TextHeader style={{ marginBottom: 15, paddingTop: 15, borderTop: '1px solid #dcdcdc' }}>
                Change Password
            </TextHeader>

            <TextFieldContainer label="Old Password">
                <PasswordTextField
                    name="oldPassword"
                    label="Old Password"
                    maxLength={255}
                    validators={['isRequired', 'disableChinese']}
                    isValidValue={(isValid) =>
                        setPasswordValidity(
                            produce((draft) => {
                                draft.oldPassword = isValid;
                            }),
                        )
                    }
                    value={passwordPostData?.oldPassword ?? ''}
                    handleChange={onPasswordUpdate}
                    forceErrorDisplay={forcePasswordValidationShow}
                />
            </TextFieldContainer>
            <TextFieldContainer label="New Password">
                <PasswordTextField
                    name="newPassword"
                    label="New Password"
                    maxLength={255}
                    validators={['isRequired', 'disableChinese']}
                    isValidValue={(isValid) =>
                        setPasswordValidity(
                            produce((draft) => {
                                draft.newPassword = isValid;
                            }),
                        )
                    }
                    value={passwordPostData?.newPassword ?? ''}
                    handleChange={onPasswordUpdate}
                    forceErrorDisplay={forcePasswordValidationShow}
                />
            </TextFieldContainer>
            <TextFieldContainer label="Confirm Password">
                <PasswordTextField
                    name="confirmNewPassword"
                    label="Confirm Password"
                    maxLength={255}
                    validators={['isRequired', 'disableChinese']}
                    isValidValue={(isValid) =>
                        setPasswordValidity(
                            produce((draft) => {
                                draft.confirmNewPassword = isValid;
                            }),
                        )
                    }
                    value={passwordPostData?.confirmNewPassword ?? ''}
                    handleChange={onPasswordUpdate}
                    forceErrorDisplay={forcePasswordValidationShow}
                />
            </TextFieldContainer>
            <div style={{ marginTop: 24 }}>
                <TextSubHeader>Password Policy:</TextSubHeader>
                <SListItem>1. Min Length Password is 8</SListItem>
                <SListItem>2. Contains at least one numeric character</SListItem>
                <SListItem>3. Contains at least one upper case</SListItem>
                <SListItem>4. Contains at least one lower case</SListItem>

                <div style={{ display: 'flex', marginTop: 20 }}>
                    <CustomButton loading={user.isLoading} onClick={onPasswordSubmit}>
                        Update
                    </CustomButton>
                </div>
            </div>
        </div>
    );
};

export default Profile;
